import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building, Store, ShoppingBag, ShoppingCart, UtensilsCrossed, Truck, Coffee, Croissant, ChevronRight } from 'lucide-react'
import { PageLayout } from './layout'
import CustomerForm from './CustomerForm'
import CustomerInvoicingModal from './CustomerInvoicingModal'
import { CreditNoteModal } from './credit-notes/CreditNoteModal'
import { CustomerDetailModal } from './customer/CustomerDetailModal'
import { fetchCustomers, fetchArchivedCustomers, fetchCustomer, archiveCustomer, unarchiveCustomer, fetchCustomerBalances, supabase } from '../supabase-client'
import { Button } from '@/components/ui/button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import ToggleSwitch from './ToggleSwitch'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import SearchInput from '@/components/ui/search-input'
import PendingRegistrationsPanel from './registration/PendingRegistrationsPanel'
import InProgressRegistrationsPanel from './registration/InProgressRegistrationsPanel'
import RecentlyRejectedSection from './registration/RecentlyRejectedSection'
import { useRegistrationPipeline } from './registration/useRegistrationPipeline'
import './CustomerDashboard.css'

const branchTypeIcons = {
  company: Building,
  branch: Store,
  retail: ShoppingBag,
  grocer: ShoppingCart,
  restaurant: UtensilsCrossed,
  distributor: Truck,
  cafe: Coffee,
  bakery: Croissant
}

const getBranchTypeIcon = (branchType) => {
  const IconComponent = branchTypeIcons[branchType] || Store
  return <IconComponent className="size-4 inline mr-1" />
}

export default function CustomerDashboard({ user, onLogout }) {
  const navigate = useNavigate()
  const [showArchived, setShowArchived] = useState(false)
  const [customers, setCustomers] = useState([])
  const [archivedCustomers, setArchivedCustomers] = useState([])
  const [allCustomers, setAllCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: 'companyName', direction: 'asc' })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState(null)
  const [invoicingModalOpen, setInvoicingModalOpen] = useState(false)
  const [invoicingCustomer, setInvoicingCustomer] = useState(null)
  // US-REC-001: Credit note creation state
  const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false)
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null)
  // Customer detail modal state
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [generatingSignupPdf, setGeneratingSignupPdf] = useState(false)
  const pipeline = useRegistrationPipeline()

  const handleDownloadSignupForm = async () => {
    try {
      setGeneratingSignupPdf(true)
      const [{ pdf }, { CustomerSignupPdf }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./CustomerSignupPdf'),
      ])
      const today = new Date()
      const dateLabel = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
      const fileDate = today.toISOString().slice(0, 10)
      const blob = await pdf(CustomerSignupPdf({ generatedDate: dateLabel })).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `GMC-New-Customer-Form-${fileDate}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      console.error('Signup form PDF generation failed:', err)
      setError('Failed to generate signup form: ' + err.message)
    } finally {
      setGeneratingSignupPdf(false)
    }
  }

  // Load customers on component mount
  useEffect(() => {
    loadCustomers()
  }, [])

  // Combine customers based on archive toggle and update display list
  useEffect(() => {
    let displayCustomers = showArchived 
      ? [...customers, ...archivedCustomers]
      : customers
    
    setAllCustomers(displayCustomers)
  }, [customers, archivedCustomers, showArchived])

  // Filter customers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCustomers(allCustomers)
    } else {
      const filtered = allCustomers.filter(customer => {
        const searchLower = searchTerm.toLowerCase()
        return (
          customer.companyName.toLowerCase().includes(searchLower) ||
          customer.email.toLowerCase().includes(searchLower) ||
          customer.branches.some(branch =>
            branch.branchName.toLowerCase().includes(searchLower)
          )
        )
      })
      setFilteredCustomers(filtered)
    }
  }, [allCustomers, searchTerm])

  const loadCustomers = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch both active and archived customers, and customer balances
      const [activeData, archivedData, balances] = await Promise.all([
        fetchCustomers(),
        fetchArchivedCustomers(),
        fetchCustomerBalances()
      ])
      
      // Transform active customers
      const transformedActiveCustomers = activeData.map(customer => {
        // Find company information branch (branch_type = 'company') first, then fall back to main office branch
        const companyBranch = customer.branches?.find(branch => branch.branch_type === 'company') ||
          customer.branches?.find(branch =>
            branch.name && (
              branch.name.toLowerCase().includes('main') ||
              branch.name.toLowerCase().includes('head') ||
              branch.name.toLowerCase().includes('office')
            )
          ) || customer.branches?.[0] // Use first branch as fallback

        // Parse company branch address into components
        const addressParts = companyBranch?.address ? companyBranch.address.split(', ').filter(part => part.trim()) : []

        return {
          id: customer.id,
          companyName: customer.name,
          email: customer.email,
          paymentTermsDays: customer.payment_terms ? parseInt(customer.payment_terms) : 30,
          payment_method: customer.payment_method || null, // Keep snake_case to match table display
          // Add customer-level contact fields from customer table and company branch
          phone: customer.phone || '', // Get phone from customer table (like email)
          addressLine1: addressParts[0] || '',
          addressLine2: addressParts[1] || '',
          addressLine3: addressParts[2] || '',
          city: addressParts[3] || '',
          postcode: addressParts[4] || '',
          country: addressParts[5] || 'United Kingdom',
          balance: balances[customer.id] || 0.00, // Calculated from invoices and payments
          // GoCardless fields for mandate display
          gocardless_customer_id: customer.gocardless_customer_id || null,
          gocardless_mandate_id: customer.gocardless_mandate_id || null,
          gocardless_mandate_status: customer.gocardless_mandate_status || null,
          // Customer logo fields
          logoFilePath: customer.logo_file_path || null,
          logoFileName: customer.logo_file_name || null,
          logoUploadedAt: customer.logo_uploaded_at || null,
          branches: customer.branches?.filter(branch =>
            // Exclude company information branches from branch count
            branch.branch_type !== 'company'
          ).map(branch => ({
            id: branch.id,
            branchName: branch.name,
            branchAddress: branch.address,
            branchPhone: branch.phone || '',
            branchEmail: branch.email || '',
            branchType: branch.branch_type || 'branch'
          })) || []
        }
      })

      // Transform archived customers and remove duplicates
      const transformedArchivedCustomers = archivedData.map(customer => {
        // Find company information branch (branch_type = 'company') first, then fall back to main office branch
        const companyBranch = customer.branches?.find(branch => branch.branch_type === 'company') ||
          customer.branches?.find(branch =>
            branch.name && (
              branch.name.toLowerCase().includes('main') ||
              branch.name.toLowerCase().includes('head') ||
              branch.name.toLowerCase().includes('office')
            )
          ) || customer.branches?.[0] // Use first branch as fallback

        // Parse company branch address into components
        const addressParts = companyBranch?.address ? companyBranch.address.split(', ').filter(part => part.trim()) : []

        return {
          id: customer.id,
          companyName: customer.name,
          email: customer.email,
          paymentTermsDays: customer.payment_terms ? parseInt(customer.payment_terms) : 30,
          payment_method: customer.payment_method || null, // Keep snake_case to match table display
          // Add customer-level contact fields from customer table and company branch
          phone: customer.phone || '', // Get phone from customer table (like email)
          addressLine1: addressParts[0] || '',
          addressLine2: addressParts[1] || '',
          addressLine3: addressParts[2] || '',
          city: addressParts[3] || '',
          postcode: addressParts[4] || '',
          country: addressParts[5] || 'United Kingdom',
          balance: balances[customer.id] || 0.00, // Calculated from invoices and payments
          // GoCardless fields for mandate display
          gocardless_customer_id: customer.gocardless_customer_id || null,
          gocardless_mandate_id: customer.gocardless_mandate_id || null,
          gocardless_mandate_status: customer.gocardless_mandate_status || null,
          // Customer logo fields
          logoFilePath: customer.logo_file_path || null,
          logoFileName: customer.logo_file_name || null,
          logoUploadedAt: customer.logo_uploaded_at || null,
          branches: customer.branches?.filter(branch =>
            // Exclude company information branches from branch count
            branch.branch_type !== 'company'
          ).map(branch => ({
            id: branch.id,
            branchName: branch.name,
            branchAddress: branch.address,
            branchPhone: branch.phone || '',
            branchEmail: branch.email || '',
            branchType: branch.branch_type || 'branch'
          })) || []
        }
      })

      // Remove duplicates based on customer ID
      const uniqueArchivedCustomers = transformedArchivedCustomers.filter((customer, index, self) => 
        index === self.findIndex(c => c.id === customer.id)
      )
      
      setCustomers(transformedActiveCustomers)
      setArchivedCustomers(uniqueArchivedCustomers)
    } catch (error) {
      console.error('Error loading customers:', error)
      setError('Failed to load customers. Please try again.')
      // No fallback to mock data - show empty state instead
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddCustomer = () => {
    // Mobile: navigate to dedicated page
    if (window.innerWidth < 768) {
      navigate('/customers/new')
      return
    }
    // Desktop: open modal
    setEditingCustomer(null)
    setIsModalOpen(true)
  }

  const handleEditCustomer = (customer) => {
    // Mobile: navigate to dedicated page
    if (window.innerWidth < 768) {
      navigate(`/customers/edit/${customer.id}`)
      return
    }
    // Desktop: open modal
    setEditingCustomer(customer)
    setFormData(null) // Clear saved data when editing existing customer
    setIsModalOpen(true)
  }

  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    // Don't clear formData here - let it persist for reopening
  }

  const handleSaveCustomer = async (customerData) => {
    try {
      // CustomerForm already handles all database operations (create/update customer + branches).
      // This callback just refreshes the local state from the DB.
      const customerId = customerData.id

      if (!customerId) {
        throw new Error('Customer data missing ID')
      }

      // Fetch the updated customer data with all branches
      const updatedCustomerFromDB = await fetchCustomer(customerId)

      // Transform the updated customer data using the same logic as loadCustomers
      const mainBranch = updatedCustomerFromDB.branches?.find(branch => branch.branch_type === 'company') ||
        updatedCustomerFromDB.branches?.find(branch =>
          branch.name && (
            branch.name.toLowerCase().includes('main') ||
            branch.name.toLowerCase().includes('head') ||
            branch.name.toLowerCase().includes('office')
          )
        ) || updatedCustomerFromDB.branches?.[0]

      const addressParts = mainBranch?.address ? mainBranch.address.split(', ').filter(part => part.trim()) : []

      // Preserve existing balance from local state (calculated from invoices/payments)
      const existingCustomer = customers.find(c => c.id === customerId) ||
                               archivedCustomers.find(c => c.id === customerId)

      const transformedCustomer = {
        id: updatedCustomerFromDB.id,
        companyName: updatedCustomerFromDB.name,
        email: updatedCustomerFromDB.email,
        paymentTermsDays: updatedCustomerFromDB.payment_terms ? parseInt(updatedCustomerFromDB.payment_terms) : 30,
        payment_method: updatedCustomerFromDB.payment_method || null,
        phone: updatedCustomerFromDB.phone || '',
        addressLine1: addressParts[0] || '',
        addressLine2: addressParts[1] || '',
        addressLine3: addressParts[2] || '',
        city: addressParts[3] || '',
        postcode: addressParts[4] || '',
        country: addressParts[5] || 'United Kingdom',
        balance: existingCustomer?.balance || 0.00,
        // GoCardless fields for mandate display
        gocardless_customer_id: updatedCustomerFromDB.gocardless_customer_id || null,
        gocardless_mandate_id: updatedCustomerFromDB.gocardless_mandate_id || null,
        gocardless_mandate_status: updatedCustomerFromDB.gocardless_mandate_status || null,
        // Customer logo fields
        logoFilePath: updatedCustomerFromDB.logo_file_path || null,
        logoFileName: updatedCustomerFromDB.logo_file_name || null,
        logoUploadedAt: updatedCustomerFromDB.logo_uploaded_at || null,
        branches: updatedCustomerFromDB.branches?.filter(branch =>
          // Exclude company information branches from branch count
          branch.branch_type !== 'company'
        ).map(branch => ({
          id: branch.id,
          branchName: branch.name,
          branchAddress: branch.address,
          branchPhone: branch.phone || '',
          branchEmail: branch.email || '',
          branchType: branch.branch_type || 'branch'
        })) || []
      }

      // Determine if this is an existing customer or a new one
      const isExisting = customers.some(c => c.id === customerId) ||
                         archivedCustomers.some(c => c.id === customerId)

      // Update the customer in the appropriate list based on their active status
      if (updatedCustomerFromDB.active) {
        if (isExisting) {
          // Update existing customer in active list
          setCustomers(prev => prev.map(customer =>
            customer.id === customerId ? transformedCustomer : customer
          ))
          // Remove from archived list if it was moved from archived to active
          setArchivedCustomers(prev => prev.filter(customer => customer.id !== customerId))
        } else {
          // Add new customer to active list
          setCustomers(prev => [...prev, transformedCustomer])
        }
      } else {
        if (isExisting) {
          // Customer was archived, move to archived list
          setCustomers(prev => prev.filter(customer => customer.id !== customerId))
          setArchivedCustomers(prev => {
            const exists = prev.some(customer => customer.id === customerId)
            if (exists) {
              return prev.map(customer => customer.id === customerId ? transformedCustomer : customer)
            }
            return [...prev, transformedCustomer]
          })
        }
      }

      // Clear form data when successfully saving
      setFormData(null)
      setIsModalOpen(false)
    } catch (error) {
      console.error('Error saving customer:', error)
      throw error
    }
  }


  const handleArchiveCustomer = async (customerId) => {
    try {
      await archiveCustomer(customerId)
      
      // Reload all customers to ensure data consistency
      await loadCustomers()
      
      setIsModalOpen(false)
    } catch (error) {
      console.error('Error archiving customer:', error)
      throw error
    }
  }

  const handleUnarchiveCustomer = async (customerId) => {
    try {
      await unarchiveCustomer(customerId)
      
      // Reload all customers to ensure data consistency
      await loadCustomers()
      
      // Switch to show active customers to display the unarchived customer
      setShowArchived(false)
      
      setIsModalOpen(false)
    } catch (error) {
      console.error('Error unarchiving customer:', error)
      throw error
    }
  }

  const handleBackToDashboard = () => {
    navigate('/')
  }

  const handleOpenInvoicing = (customer) => {
    setInvoicingCustomer(customer)
    setInvoicingModalOpen(true)
    setIsModalOpen(false) // Close the customer form modal
  }

  const handleInvoiceGenerated = () => {
    // Optionally refresh customer data or show a success message
    loadCustomers()
  }

  // US-REC-001: Handle opening credit note modal for a customer
  const handleOpenCreditNote = async (customer) => {
    try {
      // Fetch the first unpaid invoice for this customer from invoice_balances
      const { data: invoices, error } = await supabase
        .from('invoice_balances')
        .select('invoice_id, invoice_no, customer_id, amount_due, is_legacy_paid')
        .eq('customer_id', customer.id)
        .gt('amount_due', 0)
        .order('date', { ascending: false })
        .limit(1)

      if (error) throw error

      if (invoices && invoices.length > 0) {
        const inv = invoices[0]
        setCreditNoteInvoice({
          id: inv.invoice_id,
          invoice_no: inv.invoice_no,
          customer_id: inv.customer_id,
          customer_name: customer.companyName,
          amount_due: inv.amount_due,
          is_legacy_paid: inv.is_legacy_paid
        })
        setCreditNoteModalOpen(true)
      } else {
        // No unpaid invoices - could show a message or open customer form
        setError('No unpaid invoices found for this customer.')
        setTimeout(() => setError(null), 3000)
      }
    } catch (err) {
      console.error('[CustomerDashboard] Error fetching invoice for credit note:', err)
      setError('Failed to load invoice data.')
      setTimeout(() => setError(null), 3000)
    }
  }

  // US-REC-001: Handle credit note success
  const handleCreditNoteSuccess = () => {
    setCreditNoteModalOpen(false)
    setCreditNoteInvoice(null)
    // Refresh customer balances
    loadCustomers()
  }

  // Handle opening customer detail modal
  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer)
    setDetailModalOpen(true)
  }

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })

    const sortedCustomers = [...filteredCustomers].sort((a, b) => {
      let aVal = a[key]
      let bVal = b[key]

      // Handle different data types
      if (key === 'balance') {
        aVal = parseFloat(aVal) || 0
        bVal = parseFloat(bVal) || 0
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      }

      if (aVal < bVal) {
        return direction === 'asc' ? -1 : 1
      }
      if (aVal > bVal) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })

    setFilteredCustomers(sortedCustomers)
  }

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount)
  }


  if (loading) {
    return (
      <PageLayout
        title="Customer Management"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <p className="ml-3 text-muted-foreground">Loading customers...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Customer Management"
      user={user}
      onLogout={onLogout}
      headerActions={
        <>
          <SearchInput
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex items-center space-x-3">
            <ToggleSwitch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label
              htmlFor="show-archived"
              className="cursor-pointer text-sm font-semibold"
            >
              Show Archived
            </Label>
          </div>
          <Button variant="create" onClick={handleAddCustomer}>
            + Add Customer
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadSignupForm}
            disabled={generatingSignupPdf}
            title="Download blank New Customer Application form (PDF)"
          >
            {generatingSignupPdf ? 'Generating…' : 'Signup Form'}
          </Button>
        </>
      }
    >
      <div className="h-full overflow-y-auto">
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
            </div>
          )}

          {!pipeline.loading && (
            <>
              <PendingRegistrationsPanel rows={pipeline.pending} />
              <InProgressRegistrationsPanel rows={pipeline.inProgress} />
              <RecentlyRejectedSection rows={pipeline.rejected} />
            </>
          )}

          {/* Customer Table */}
          <div className="sales-form-container">
            
            {filteredCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-4xl mb-4">👥</div>
                <h3 className="text-lg font-semibold mb-2">No customers found</h3>
                <p className="text-gray-600 mb-4">
                  {searchTerm
                    ? `No customers match "${searchTerm}"`
                    : customers.length === 0 && archivedCustomers.length === 0
                      ? "Start by adding your first customer"
                      : "No customers match the current filter"
                  }
                </p>
                {customers.length === 0 && archivedCustomers.length === 0 && !searchTerm && (
                  <Button onClick={handleAddCustomer}>
                    Add Your First Customer
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table - hidden on mobile */}
                <Table className="hidden md:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead onClick={() => handleSort('companyName')} className="cursor-pointer hover:bg-muted/50">
                        Name {getSortIcon('companyName')}
                      </TableHead>
                      <TableHead>
                        Branches
                      </TableHead>
                      <TableHead>
                        Payment Method
                      </TableHead>
                      <TableHead onClick={() => handleSort('balance')} className="cursor-pointer hover:bg-muted/50">
                        Balance {getSortIcon('balance')}
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map(customer => (
                      <TableRow key={customer.id}>
                        <TableCell className="font-semibold">
                          <button
                            onClick={() => handleCustomerClick(customer)}
                            className="text-left hover:text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded transition-colors"
                          >
                            {customer.companyName}
                          </button>
                        </TableCell>
                        <TableCell className="branches-cell">
                          {customer.branches.length > 0 ? (
                            <div className="branches-list">
                              {customer.branches.map((branch, index) => (
                                <div key={index} className="branch-item">
                                  <span className="flex items-center gap-1">
                                    {getBranchTypeIcon(branch.branchType)}
                                    {branch.branchName}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">No branches</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {customer.payment_method ? (
                            <Badge variant="secondary">
                              {customer.payment_method === 'xero' && 'Xero'}
                              {customer.payment_method === 'gocardless' && 'GoCardless'}
                              {customer.payment_method === 'cash' && 'Cash'}
                              {/* Legacy payment methods (deprecated) */}
                              {customer.payment_method === 'bank_transfer' && 'Bank Transfer'}
                              {customer.payment_method === 'cheque' && 'Cheque'}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">Not set</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono font-semibold">
                          <span className={`${
                            customer.balance > 0 ? 'text-red-600' :
                            customer.balance < 0 ? 'text-green-600' :
                            'text-gray-600'
                          }`}>
                            {formatCurrency(customer.balance)}
                          </span>
                        </TableCell>
                        <TableCell className="actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCustomer(customer)}
                            title="Edit customer"
                          >
                            <EditIcon />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Mobile Cards - visible only on mobile */}
                <div className="mobile-customer-cards md:hidden">
                  {filteredCustomers.map(customer => (
                    <div
                      key={customer.id}
                      className="mobile-customer-card"
                      onClick={() => handleCustomerClick(customer)}
                    >
                      {/* Header: Name and Balance */}
                      <div className="mobile-card-header">
                        <span className="mobile-card-name">{customer.companyName}</span>
                        <span className={`mobile-card-balance ${
                          customer.balance > 0 ? 'outstanding' :
                          customer.balance < 0 ? 'credit' : ''
                        }`}>
                          {formatCurrency(customer.balance)}
                        </span>
                      </div>

                      {/* Branches */}
                      {customer.branches.length > 0 && (
                        <div className="mobile-card-branches">
                          {customer.branches.slice(0, 3).map((branch, index) => (
                            <span key={index} className="mobile-branch-item">
                              {getBranchTypeIcon(branch.branchType)}
                              {branch.branchName}
                            </span>
                          ))}
                          {customer.branches.length > 3 && (
                            <span className="mobile-branch-more">
                              +{customer.branches.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Footer: Payment Method and Chevron */}
                      <div className="mobile-card-footer">
                        {customer.payment_method ? (
                          <Badge variant="secondary" className="text-xs">
                            {customer.payment_method === 'xero' && 'Xero'}
                            {customer.payment_method === 'gocardless' && 'GoCardless'}
                            {customer.payment_method === 'cash' && 'Cash'}
                            {customer.payment_method === 'bank_transfer' && 'Bank Transfer'}
                            {customer.payment_method === 'cheque' && 'Cheque'}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">No payment method</span>
                        )}
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
      </div>

      {/* Customer Form Modal */}
      <CustomerForm
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveCustomer}
        editingCustomer={editingCustomer}
        savedFormData={formData}
        onFormDataChange={handleFormDataChange}
        isArchived={editingCustomer && archivedCustomers.some(c => c.id === editingCustomer.id)}
        onArchive={handleArchiveCustomer}
        onUnarchive={handleUnarchiveCustomer}
        onInvoicing={handleOpenInvoicing}
      />

      {/* Customer Invoicing Modal */}
      <CustomerInvoicingModal
        isOpen={invoicingModalOpen}
        onClose={() => {
          setInvoicingModalOpen(false)
          setInvoicingCustomer(null)
        }}
        customer={invoicingCustomer}
        onInvoiceGenerated={handleInvoiceGenerated}
      />

      {/* US-REC-001: Credit Note Modal */}
      {creditNoteInvoice && (
        <CreditNoteModal
          invoice={creditNoteInvoice}
          open={creditNoteModalOpen}
          onOpenChange={(open) => {
            setCreditNoteModalOpen(open)
            if (!open) setCreditNoteInvoice(null)
          }}
          onSuccess={handleCreditNoteSuccess}
        />
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          open={detailModalOpen}
          onOpenChange={(open) => {
            setDetailModalOpen(open)
            if (!open) setSelectedCustomer(null)
          }}
          onSave={handleSaveCustomer}
        />
      )}
    </PageLayout>
  )
}